import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './SkipLinks.css';

/**
 * #52 fix (2026-05-17): only render skip links whose targets actually exist
 * on the current page. The previous unconditional rendering produced
 * "Skip to chat" on every route — including pages with no chat — which is
 * a WCAG 2.4.1 Bypass-Blocks failure (a link that goes nowhere).
 *
 * FIXED 2026-09-05, twice over:
 *
 *   NO ELEMENT IN THE APP HAD ANY OF THESE IDS. The note below used to read
 *   "pair this with adding `id="main-content"` …" as a to-do, and it was never
 *   done — so `presentIds` was always empty and this component rendered `null`
 *   on every route. Skip links had never appeared for anyone.
 *
 *   THE SCAN RAN ONCE, ON MOUNT. This sits above the `<Routes>` in the app
 *   shell, so on first mount the route's content has not rendered yet (and a
 *   lazy route certainly has not). Even with the ids in place it would have
 *   found nothing, and never looked again. It now re-scans on every route
 *   change, and again on the next frame so a lazily-loaded route is not
 *   missed.
 */
const TARGETS = [
  { id: 'main-content', label: 'Skip to main content' },
  { id: 'navigation', label: 'Skip to navigation' },
  { id: 'chat', label: 'Skip to chat' },
];

export const SkipLinks: React.FC = () => {
  const [presentIds, setPresentIds] = useState<string[]>([]);
  const location = useLocation();

  useEffect(() => {
    const scan = () => {
      const found = TARGETS.filter((t) => document.getElementById(t.id) !== null);
      setPresentIds((previous) => {
        const next = found.map((t) => t.id);
        // Avoid a render when nothing changed: this runs on every navigation.
        if (previous.length === next.length && previous.every((id, i) => id === next[i])) {
          return previous;
        }
        return next;
      });
    };

    // Immediately, for a route that is already loaded...
    scan();
    // ...and after paint, for a lazily-loaded one that has not mounted yet.
    const raf = window.requestAnimationFrame(scan);
    // A chunk still in flight needs longer than a frame.
    const timer = window.setTimeout(scan, 400);

    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  const visible = TARGETS.filter((t) => presentIds.includes(t.id));
  if (visible.length === 0) return null;

  return (
    <nav className="skip-links" aria-label="Skip Links">
      {visible.map((t) => (
        <a key={t.id} href={`#${t.id}`} className="skip-link">
          {t.label}
        </a>
      ))}
    </nav>
  );
};
